import {
  Component,
  ComponentRef,
  computed,
  inject,
  inputBinding,
  resource,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { AuthStore } from '@core/auth/auth-store';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { Button } from '@shared/components/button/button';
import { DateTimeInput } from '@shared/components/date-time-input/date-time-input';
import { EmailTagInput } from '@shared/components/email-tag-input/email-tag-input';
import { Item } from '@shared/components/item/item';
import { Select, SelectItem } from '@shared/components/select/select';
import { Toast, ToastMessage } from '@shared/components/toast/toast';

@Component({
  selector: 'app-invitations-page',
  imports: [Item, EmailTagInput, DateTimeInput, Select, Button],
  providers: [ApiClient],
  templateUrl: './invitations-page.html',
})
export class InvitationsPage {
  // Auth
  private readonly authStore = inject(AuthStore);
  private readonly jwtDecoder = inject(JwtDecoderService);

  // Services
  private readonly apiClient = inject(ApiClient);

  // Toast
  private toastRef: ComponentRef<Toast> | null = null;

  // Constants
  private static readonly BUTTON_ENABLE_DELAY = 1000;

  // Signals
  readonly testSetData = resource({
    loader: () => this.apiClient.getTestSets(),
  });

  readonly loading = computed(() => this.testSetData.status() === 'loading');
  readonly selectedRole = signal<SelectItem>({
    label: 'All Roles',
    value: 'all',
  });
  readonly isSubmitting = signal(false);
  readonly toastMessage = signal<string>('');

  readonly selectedTestSetId = signal<string | null>(null);

  readonly emailTagInputValue = signal<string[]>([]);
  readonly emailTagInputError = signal<string>('');

  readonly availableFromValue = signal<string>('');
  readonly availableUntilValue = signal<string>('');
  readonly availableFromError = signal<string>('');
  readonly availableUntilError = signal<string>('');

  readonly selectItems = signal<SelectItem[]>([
    { value: 'all', label: 'All Roles' },
    { value: 'senior', label: 'Senior Role' },
    { value: 'mid', label: 'Middle Role' },
    { value: 'junior', label: 'Junior Role' },
  ]);

  readonly testSetItems = computed(() => {
    const data = this.testSetData.value() || [];
    const roleValue = this.selectedRole()?.value || 'all';
    return (
      roleValue === 'all'
        ? data
        : data.filter(testSet => testSet.difficulty === roleValue)
    ).filter(testSet => testSet.duration > 0);
  });

  readonly canSubmit = computed(
    () =>
      !!this.selectedTestSetId() &&
      this.emailTagInputValue().length > 0 &&
      !this.emailTagInputError() &&
      !this.availableFromError() &&
      !!this.availableFromValue() &&
      !this.availableUntilError() &&
      !!this.availableUntilValue()
  );

  // View children
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  readonly emailTagInput = viewChild.required(EmailTagInput);

  // Toast
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  // Submit
  async handleSubmit(event: Event): Promise<void> {
    if (!this.canSubmit() || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const form = event.target as HTMLFormElement;
    const emails = this.emailTagInputValue();

    if (!emails?.length) {
      this.showToast('warning', 'Please add at least one email address');
      this.enableButtonAfterDelay();
      return;
    }

    await this.sendInvitations(emails);
    this.resetForm(form);
    this.enableButtonAfterDelay();
  }

  private async sendInvitations(emails: string[]): Promise<void> {
    for (const invitedEmail of emails) {
      try {
        await this.apiClient.sentEmail({
          testSetId: this.selectedTestSetId(),
          invitedEmail,
          scheduledAt: this.getDateTimeValue('availableFrom'),
          expiredAt: this.getDateTimeValue('availableUntil'),
          userId: this.jwtDecoder.getUserId(this.authStore.getAccessToken()),
        });
        this.showToast('success', `ส่งสำเร็จ: ${invitedEmail}`);
      } catch {
        this.showToast('error', `เกิดข้อผิดพลาด: ${invitedEmail}`);
      }
    }
  }

  private getDateTimeValue(
    fieldName: 'availableFrom' | 'availableUntil'
  ): string {
    const value =
      fieldName === 'availableFrom'
        ? this.availableFromValue()
        : this.availableUntilValue();
    if (!value) return new Date().toISOString();

    const date = new Date(value);
    if (fieldName === 'availableUntil') date.setHours(23, 59, 59, 999);
    return date.toISOString();
  }

  private resetForm(form: HTMLFormElement): void {
    form.reset();
    this.emailTagInput().resetTags();
    this.availableFromValue.set('');
    this.availableUntilValue.set('');
    this.testSetData.reload();
  }

  private enableButtonAfterDelay(): void {
    setTimeout(
      () => this.isSubmitting.set(false),
      InvitationsPage.BUTTON_ENABLE_DELAY
    );
  }
}
